import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Upload, Save } from 'lucide-react';

export const HeroManager = () => {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    badge_text: '',
    badges: [],
    cta_text: '',
    cta_url: '',
    product_box_image_url: '',
    hero_background_image_url: '',
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchHeroContent();
  }, []);

  const fetchHeroContent = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/hero`);
      setFormData(response.data);
    } catch (error) {
      console.error('Error fetching hero content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/hero`, formData, {
        headers: getAuthHeader(),
      });
      alert('Hero content updated successfully!');
    } catch (error) {
      console.error('Error updating hero content:', error);
      alert('Failed to update hero content');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (file) => {
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API_URL}/api/upload`, formDataUpload, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data',
        },
      });

      setFormData({
        ...formData,
        product_box_image_url: response.data.url,
        product_box_image_filename: response.data.filename,
      });
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload image');
    }
  };

  const handleBackgroundUpload = async (file) => {
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API_URL}/api/upload`, formDataUpload, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data',
        },
      });

      setFormData({
        ...formData,
        hero_background_image_url: response.data.url,
        hero_background_image_filename: response.data.filename,
      });
    } catch (error) {
      console.error('Error uploading background:', error);
      alert('Failed to upload background image');
    }
  };

  if (loading) {
    return <div className="text-white">Loading...</div>;
  }

  return (
    <div className="max-w-4xl">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <h3 className="text-xl font-bold text-white mb-4">Hero Section Content</h3>

          <div className="space-y-4">
            <div>
              <label className="block text-white text-sm font-medium mb-2">
                Badge Text
              </label>
              <input
                type="text"
                value={formData.badge_text}
                onChange={(e) =>
                  setFormData({ ...formData, badge_text: e.target.value })
                }
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <div>
              <label className="block text-white text-sm font-medium mb-2">
                Main Title
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <div>
              <label className="block text-white text-sm font-medium mb-2">
                Subtitle
              </label>
              <textarea
                value={formData.subtitle}
                onChange={(e) =>
                  setFormData({ ...formData, subtitle: e.target.value })
                }
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                rows="2"
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-3">
                <label className="block text-white text-sm font-medium">
                  Feature Badges (3 badges below CTA button)
                </label>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, badges: [...(formData.badges || []), ''] })}
                  className="text-sm px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Add Badge
                </button>
              </div>
              <div className="space-y-2">
                {(formData.badges || []).map((badge, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={badge}
                      onChange={(e) => {
                        const newBadges = [...formData.badges];
                        newBadges[index] = e.target.value;
                        setFormData({ ...formData, badges: newBadges });
                      }}
                      placeholder="e.g., Tournament Grade"
                      className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const newBadges = formData.badges.filter((_, i) => i !== index);
                        setFormData({ ...formData, badges: newBadges });
                      }}
                      className="px-3 py-2 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                {(!formData.badges || formData.badges.length === 0) && (
                  <p className="text-gray-400 text-sm">No badges added yet</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-white text-sm font-medium mb-2">
                  CTA Button Text
                </label>
                <input
                  type="text"
                  value={formData.cta_text}
                  onChange={(e) =>
                    setFormData({ ...formData, cta_text: e.target.value })
                  }
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-white text-sm font-medium mb-2">
                  CTA Button URL
                </label>
                <input
                  type="url"
                  value={formData.cta_url}
                  onChange={(e) =>
                    setFormData({ ...formData, cta_url: e.target.value })
                  }
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-white text-sm font-medium mb-2">
                Product Box Image
              </label>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={formData.product_box_image_url}
                  onChange={(e) =>
                    setFormData({ ...formData, product_box_image_url: e.target.value })
                  }
                  className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Image URL"
                />
                <label className="cursor-pointer">
                  <div className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                    <Upload className="w-5 h-5 mr-2" />
                    Upload
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      if (e.target.files?.[0]) {
                        handleFileUpload(e.target.files[0]);
                      }
                    }}
                    className="hidden"
                  />
                </label>
              </div>
              {formData.product_box_image_url && (
                <img
                  src={formData.product_box_image_url}
                  alt="Product box preview"
                  className="mt-3 h-40 object-contain bg-gray-900 rounded"
                />
              )}
            </div>

            <div>
              <label className="block text-white text-sm font-medium mb-2">
                Hero Background Image
              </label>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={formData.hero_background_image_url}
                  onChange={(e) =>
                    setFormData({ ...formData, hero_background_image_url: e.target.value })
                  }
                  className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Background Image URL"
                />
                <label className="cursor-pointer">
                  <div className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                    <Upload className="w-5 h-5 mr-2" />
                    Upload
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      if (e.target.files?.[0]) {
                        handleBackgroundUpload(e.target.files[0]);
                      }
                    }}
                    className="hidden"
                  />
                </label>
              </div>
              {formData.hero_background_image_url && (
                <img
                  src={formData.hero_background_image_url}
                  alt="Hero background preview"
                  className="mt-3 h-40 w-full object-cover bg-gray-900 rounded"
                />
              )}
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="flex items-center px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
        >
          <Save className="w-5 h-5 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </form>
    </div>
  );
};